/**
 * Which photograph stands for a case in the list, and what to show when there
 * is none.
 *
 * Exterior first — a building is recognised from outside — then a room, then
 * whatever else was shot. A motor claim is recognised by its front three
 * quarters, which is why that list prefers frontLeft; the principle is the
 * same and only the order differs, so the order is per client
 * (`listImageSides` in src/config/clients/<key>.json).
 */

/** Ergo property's own order, and the reason this module exists. */
export const PROPERTY_SIDES = ["exterior", "interior", "additional"];

/** Property rows are read upload first; the motor list reads them the other way round. */
const UPLOAD_FIRST = ["upload_picture", "picture"];

/** The existing design's order for a motor row: the ROI crop before the original upload. */
export const PICTURE_FIRST = ["picture", "upload_picture"];

const pathOf = (entry, fields = UPLOAD_FIRST) => {
  if (!entry) return null;
  if (typeof entry === "string") return entry || null;
  for (const field of fields) {
    if (entry[field]) return entry[field];
  }
  return null;
};

/** The API mixes camelCase and snake_case for sides; compare them as one. */
const sideKey = (side) =>
  typeof side === "string" ? side.replace(/[_\s-]+/g, "").toLowerCase() : "";

/**
 * A path from `car_images`, honouring the preference order.
 *
 * `car_images` is `{ side: [ {picture} ] }` on the list row, but a side is
 * accepted as a bare object or string too — the shape varies across these
 * deployments and a thumbnail is not worth failing over.
 */
export const pickSideImage = (
  carImages,
  sides = PROPERTY_SIDES,
  fields = UPLOAD_FIRST,
) => {
  if (!carImages || typeof carImages !== "object") return null;

  const byKey = new Map(
    Object.entries(carImages).map(([side, value]) => [sideKey(side), value]),
  );

  for (const side of sides) {
    const value = byKey.get(sideKey(side));
    const candidates = Array.isArray(value) ? value : [value];
    for (const candidate of candidates) {
      const path = pathOf(candidate, fields);
      if (path) return path;
    }
  }
  return null;
};

/**
 * The existing design's order for a motor list: the front, then its two
 * three-quarter views, then round the car. Kept identical so the two designs
 * put the same photograph on the same row.
 */
export const MOTOR_SIDES = [
  "front",
  "frontLeft",
  "frontRight",
  "left",
  "right",
  "rear",
  "rearLeft",
  "rearRight",
];

/**
 * The photograph for a list row: the preferred sides first, then ANY side
 * that has one.
 *
 * The fallback is what makes this safe across deployments. A list that names
 * its sides differently, or carries only a top-down shot, still yields the
 * case's own photograph rather than a placeholder — and pickSideImage already
 * accepts a side as an array, a bare object or a string, which is the shape
 * difference that hid every Bajaj thumbnail in the redesign:
 *
 *   Bajaj   car_images: { front: { picture: "https://…/roi/image3.jpeg" } }
 *   Ergo    car_images: { front: [ { picture: "/home/camcom/…" } ] }
 */
export const pickListImage = (carImages, sides = MOTOR_SIDES) => {
  // Same field order as the existing design (Cases.js): a side object that
  // carries both shows its ROI `picture`, not the original upload.
  const preferred = pickSideImage(carImages, sides, PICTURE_FIRST);
  if (preferred) return preferred;
  if (!carImages || typeof carImages !== "object") return null;
  return pickSideImage(carImages, Object.keys(carImages), PICTURE_FIRST);
};

/**
 * The same choice from `property_data`, which is what GET /vehicle returns:
 * a flat list where each photograph carries its own `side`.
 *
 * Needed because the list row's `car_images` is empty for the property claims
 * seen so far, and this is the only other place the photographs appear.
 */
export const pickPropertyDataImage = (propertyData, sides = PROPERTY_SIDES) => {
  if (!Array.isArray(propertyData)) return null;

  for (const side of sides) {
    const match = propertyData.find(
      (entry) => sideKey(entry?.side) === sideKey(side) && pathOf(entry),
    );
    if (match) return pathOf(match);
  }

  // A photograph with an unrecognised side is still a photograph of this
  // property, and beats a stock illustration.
  const any = propertyData.find((entry) => pathOf(entry));
  return any ? pathOf(any) : null;
};

/**
 * The stand-in when a case has no photograph at all.
 *
 * These replaced a rotation through seven stock car photographs picked by a
 * hash of the case id. That put a photograph of somebody else's car under a
 * claim, which does not read as "no image available" — it reads as this
 * claim's vehicle, which is worse than showing nothing.
 *
 * Inline rather than assets: they are placeholders, and a repository already
 * carrying 60 MB of 3D models does not need more binaries for them. Drawn in
 * the app's own neutrals, as line art, so they read as an absence rather than
 * as a photograph that failed to load, and at the row's 104x58 ratio so they
 * do not distort.
 */
const placeholder = (body) =>
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 104 58">
      <rect width="104" height="58" fill="#f3f1ed"/>
      <g fill="none" stroke="#c4c8cd" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round">${body}</g>
    </svg>`.replace(/\s+/g, " "),
  );

export const CAR_PLACEHOLDER = placeholder(
  `<path d="M30 36h44"/>
   <path d="M34 36l3-9a4 4 0 0 1 4-3h22a4 4 0 0 1 4 3l3 9"/>
   <circle cx="41" cy="38" r="3.5"/>
   <circle cx="63" cy="38" r="3.5"/>
   <path d="M43 27h18"/>`,
);

export const HOUSE_PLACEHOLDER = placeholder(
  `<path d="M34 30 52 18l18 12"/>
   <path d="M38 30v11h28V30"/>
   <path d="M48 41v-6h8v6"/>`,
);

/** The placeholder for a client's line of business. */
export const placeholderFor = (inspectionLine) =>
  inspectionLine === "property" ? HOUSE_PLACEHOLDER : CAR_PLACEHOLDER;

/**
 * The first usable picture out of an assessment's uploads.
 *
 * For the clients whose case list carries no image, the thumbnail comes from
 * the detail call instead. Order is the API's own: the adapter has already put
 * the ROI set in the order the backend gave, and preferring one side over
 * another would mean guessing which side a reader wants for a vehicle whose
 * damage could be anywhere.
 */
export const pickAssessmentImage = (detail) => {
  const uploads = Array.isArray(detail?.uploads) ? detail.uploads : [];
  for (const upload of uploads) {
    const picture = upload?.upload_picture || upload?.picture;
    if (typeof picture === "string" && picture.trim()) return picture.trim();
  }
  return null;
};
